import { useState, useEffect } from 'react';
import {
  Loader2, Plus, X, Share2, Linkedin, Youtube, CheckCircle, Trash2, ChevronDown
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PLATFORM_META = {
  linkedin: { label: 'LinkedIn', icon: Linkedin, color: '#0077B5', maxChars: 3000 },
  youtube: { label: 'YouTube', icon: Youtube, color: '#FF0000', maxChars: 5000 },
};

const SocialTab = ({ headers }) => {
  const [posts, setPosts] = useState([]);
  const [blogArticles, setBlogArticles] = useState([]);
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [composerData, setComposerData] = useState({
    source_type: 'blog', source_slug: '', source_title: '', platform: 'linkedin', content: '', hashtags: '',
  });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [viewMode, setViewMode] = useState('list');
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [autoSettings, setAutoSettings] = useState({ auto_post_enabled: false, auto_post_platforms: ['linkedin'], auto_post_content_types: ['blog'] });
  const [linkedinStatus, setLinkedinStatus] = useState(null);
  const [linkedinPosts, setLinkedinPosts] = useState([]);
  const [postingSlug, setPostingSlug] = useState(null);

  const fetchLinkedinStatus = async () => {
    try {
      const res = await fetch(`${API}/api/linkedin/status`, { headers, credentials: 'include' });
      if (res.ok) setLinkedinStatus(await res.json());
    } catch (e) { console.error('Failed to fetch LinkedIn status:', e); }
  };

  const fetchLinkedinPosts = async () => {
    try {
      const res = await fetch(`${API}/api/linkedin/posts`, { headers, credentials: 'include' });
      if (res.ok) { const d = await res.json(); setLinkedinPosts(d.posts || []); }
    } catch (e) { console.error('Failed to fetch LinkedIn posts:', e); }
  };

  const connectLinkedin = async () => {
    try {
      const res = await fetch(`${API}/api/linkedin/connect`, { headers, credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        window.open(data.auth_url, '_blank');
      }
    } catch (e) { console.error('Failed to initiate LinkedIn connect:', e); }
  };

  const postBlogToLinkedin = async (slug) => {
    setPostingSlug(slug);
    try {
      const res = await fetch(`${API}/api/linkedin/post-blog/${slug}`, { method: 'POST', headers, credentials: 'include' });
      if (res.ok) { fetchLinkedinPosts(); alert('Posted to LinkedIn!'); }
      else { const d = await res.json(); alert(d.detail || 'Failed to post'); }
    } catch (e) { console.error('Failed to post blog to LinkedIn:', e); }
    finally { setPostingSlug(null); }
  };

  const fetchAll = async () => {
    try {
      const [postsRes, blogRes, newsRes, settingsRes] = await Promise.all([
        fetch(`${API}/api/admin/social/posts`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/blog`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/news`, { headers, credentials: 'include' }),
        fetch(`${API}/api/admin/social/settings`, { headers, credentials: 'include' }),
      ]);
      if (postsRes.ok) setPosts((await postsRes.json()).posts || []);
      if (blogRes.ok) setBlogArticles((await blogRes.json()).articles || []);
      if (newsRes.ok) setNewsArticles((await newsRes.json()).articles || []);
      if (settingsRes.ok) { const s = await settingsRes.json(); if (s.auto_post_enabled !== undefined) setAutoSettings(s); }
    } catch (e) { console.error('Failed to fetch social data:', e); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on mount only
  useEffect(() => { fetchAll(); fetchLinkedinStatus(); fetchLinkedinPosts(); }, []);

  const handleSourceChange = (e) => {
    const [type, slug] = e.target.value.split('::');
    const allContent = [...blogArticles.map(a => ({ ...a, _type: 'blog' })), ...newsArticles.map(a => ({ ...a, _type: 'news' }))];
    const item = allContent.find(a => (a.slug || a.hash) === slug);
    setComposerData(prev => ({
      ...prev,
      source_type: type,
      source_slug: slug,
      source_title: item?.title || '',
      content: type === 'blog'
        ? `${item?.title || ''}\n\n${item?.excerpt || ''}\n\nRead more on our blog.`
        : `${item?.title || ''}\n\n${item?.summary || ''}\n\nRead the full story on our website.`,
    }));
  };

  const handleCreate = async () => {
    setSaving(true);
    try {
      const body = {
        ...composerData,
        hashtags: composerData.hashtags.split(',').map(h => h.trim()).filter(Boolean),
      };
      const res = await fetch(`${API}/api/admin/social/posts`, { method: 'POST', headers, credentials: 'include', body: JSON.stringify(body) });
      if (res.ok) {
        setShowComposer(false);
        setComposerData({ source_type: 'blog', source_slug: '', source_title: '', platform: 'linkedin', content: '', hashtags: '' });
        fetchAll();
      }
    } catch (e) { console.error('Failed to create social post:', e); }
    finally { setSaving(false); }
  };

  const handlePublish = async (postId) => {
    await fetch(`${API}/api/admin/social/posts/${postId}/publish`, { method: 'POST', headers, credentials: 'include' });
    fetchAll();
  };

  const handleDelete = async (postId) => {
    await fetch(`${API}/api/admin/social/posts/${postId}`, { method: 'DELETE', headers, credentials: 'include' });
    fetchAll();
  };

  if (loading) return <Loader2 className="w-6 h-6 animate-spin text-taxera-blue" />;

  const filteredPosts = filter === 'all' ? posts : posts.filter(p => p.status === filter);

  return (
    <div data-testid="admin-social">
      {/* LinkedIn Connection Status */}
      <div className="bg-white rounded-2xl border border-slate-200 p-5 mb-6" data-testid="linkedin-connection">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#0077B5]/10 flex items-center justify-center">
              <Linkedin className="w-5 h-5 text-[#0077B5]" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-taxera-charcoal">LinkedIn Company Page</h3>
              {linkedinStatus?.connected ? (
                <p className="text-xs text-emerald-600">Connected — token valid for {linkedinStatus.expires_in_days} days</p>
              ) : (
                <p className="text-xs text-slate-400">Not connected</p>
              )}
            </div>
          </div>
          {linkedinStatus?.connected ? (
            <span className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-50 text-emerald-700 rounded-full text-xs font-semibold">
              <CheckCircle className="w-3.5 h-3.5" /> Connected
            </span>
          ) : (
            <button onClick={connectLinkedin} className="px-4 py-2 bg-[#0077B5] text-white rounded-lg text-xs font-semibold hover:bg-[#005f94] transition-colors" data-testid="linkedin-connect-btn">
              Connect LinkedIn
            </button>
          )}
        </div>
        {linkedinStatus?.connected && blogArticles.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-2xs font-bold text-slate-400 uppercase mb-2">Quick Post to LinkedIn</p>
            <div className="flex flex-wrap gap-2">
              {blogArticles.slice(0, 5).map(a => (
                <button key={a.slug} onClick={() => postBlogToLinkedin(a.slug)} disabled={postingSlug === a.slug}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 hover:bg-[#0077B5]/5 hover:border-[#0077B5]/30 transition-colors disabled:opacity-50"
                  data-testid={`linkedin-post-${a.slug}`}>
                  <Share2 className="w-3 h-3" />
                  {postingSlug === a.slug ? 'Posting...' : a.title?.slice(0, 40)}
                </button>
              ))}
            </div>
          </div>
        )}
        {linkedinPosts.length > 0 && (
          <div className="mt-4 pt-4 border-t border-slate-100">
            <p className="text-2xs font-bold text-slate-400 uppercase mb-2">Recent LinkedIn Posts ({linkedinPosts.length})</p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {linkedinPosts.slice(0, 5).map((p, i) => (
                <div key={`lp-${p.posted_at || i}`} className="flex items-center justify-between text-xs">
                  <span className="text-slate-600 truncate max-w-[300px]">{p.commentary}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${p.success ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {p.success ? 'Posted' : 'Failed'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-6">
        <span className="text-xs text-slate-400">{posts.length} posts</span>
        <button onClick={() => setShowComposer(!showComposer)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-taxera-teal text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all"
          data-testid="social-compose-btn">
          {showComposer ? <X className="w-3.5 h-3.5" /> : <Plus className="w-3.5 h-3.5" />}
          {showComposer ? 'Cancel' : 'New Post'}
        </button>
      </div>

      {showComposer && (
        <div className="bg-white rounded-2xl border border-slate-200 p-6 mb-6" data-testid="social-composer">
          <h3 className="font-heading text-base font-bold text-taxera-charcoal mb-4">Compose Social Post</h3>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Source content</label>
              <select value={`${composerData.source_type}::${composerData.source_slug}`} onChange={handleSourceChange}
                className="w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-taxera-teal" data-testid="social-source-select">
                <option value="custom::">Custom post</option>
                <optgroup label="Blog Articles">
                  {blogArticles.map(a => <option key={a.slug} value={`blog::${a.slug}`}>{a.title}</option>)}
                </optgroup>
                <optgroup label="News Articles">
                  {newsArticles.map(a => <option key={a.hash} value={`news::${a.hash}`}>{a.title}</option>)}
                </optgroup>
              </select>
            </div>
            <div>
              <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Platform</label>
              <div className="flex gap-2">
                {Object.entries(PLATFORM_META).map(([key, meta]) => {
                  const Icon = meta.icon;
                  return (
                    <button key={key} onClick={() => setComposerData(prev => ({ ...prev, platform: key }))}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold transition-colors ${
                        composerData.platform === key ? 'text-white' : 'bg-slate-100 text-slate-500'
                      }`}
                      style={composerData.platform === key ? { backgroundColor: meta.color } : {}}
                      data-testid={`social-platform-${key}`}>
                      <Icon className="w-3.5 h-3.5" /> {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
          <div className="mb-4">
            <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">
              Content ({composerData.content.length}/{PLATFORM_META[composerData.platform]?.maxChars || 3000})
            </label>
            <textarea value={composerData.content} onChange={(e) => setComposerData(prev => ({ ...prev, content: e.target.value }))}
              rows={6} className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-taxera-teal resize-none"
              placeholder="Write your post content..." data-testid="social-content-input" />
          </div>
          <div className="mb-4">
            <label className="text-2xs font-bold text-slate-400 uppercase tracking-wide block mb-1.5">Hashtags (comma-separated)</label>
            <input type="text" value={composerData.hashtags} onChange={(e) => setComposerData(prev => ({ ...prev, hashtags: e.target.value }))}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-taxera-teal"
              placeholder="#TaxTech, #eInvoicing, #SAP" data-testid="social-hashtags-input" />
          </div>
          <button onClick={handleCreate} disabled={!composerData.content || saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-taxera-blue text-white rounded-full text-sm font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="social-save-btn">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
            Save as Draft
          </button>
        </div>
      )}

      <div className="bg-slate-50 rounded-xl border border-slate-200 p-4 mb-5" data-testid="social-auto-settings">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => {
              const next = { ...autoSettings, auto_post_enabled: !autoSettings.auto_post_enabled };
              setAutoSettings(next);
              fetch(`${API}/api/admin/social/settings`, { method: 'PUT', headers, credentials: 'include', body: JSON.stringify(next) });
            }} className={`w-10 h-6 rounded-full transition-colors relative ${autoSettings.auto_post_enabled ? 'bg-taxera-teal' : 'bg-slate-300'}`}
              data-testid="social-auto-toggle">
              <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${autoSettings.auto_post_enabled ? 'left-5' : 'left-1'}`} />
            </button>
            <div>
              <span className="text-sm font-semibold text-slate-700">Auto-post new content</span>
              <p className="text-2xs text-slate-400">Automatically create draft posts when new blog articles or news are published</p>
            </div>
          </div>
        </div>
        {autoSettings.auto_post_enabled && (
          <div className="flex flex-wrap gap-2 mt-2">
            <span className="text-2xs text-slate-400 self-center mr-1">Platforms:</span>
            {Object.entries(PLATFORM_META).map(([key, meta]) => {
              const Icon = meta.icon;
              const active = autoSettings.auto_post_platforms?.includes(key);
              return (
                <button key={key} onClick={() => {
                  const platforms = active ? autoSettings.auto_post_platforms.filter(p => p !== key) : [...(autoSettings.auto_post_platforms || []), key];
                  const next = { ...autoSettings, auto_post_platforms: platforms };
                  setAutoSettings(next);
                  fetch(`${API}/api/admin/social/settings`, { method: 'PUT', headers, credentials: 'include', body: JSON.stringify(next) });
                }} className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-2xs font-semibold transition-colors ${active ? 'text-white' : 'bg-white text-slate-400 border border-slate-200'}`}
                  style={active ? { backgroundColor: meta.color } : {}}>
                  <Icon className="w-3 h-3" /> {meta.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {['all', 'draft', 'scheduled', 'published'].map((f) => (
            <button key={f} onClick={() => setFilter(f)}
              className={`text-2xs px-3 py-1.5 rounded-full font-semibold transition-colors ${filter === f ? 'bg-taxera-charcoal text-white' : 'bg-white text-slate-500 border border-slate-200 hover:border-slate-300'}`}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === 'list' ? 'bg-white text-taxera-charcoal shadow-sm' : 'text-slate-400'}`} data-testid="social-view-list">List</button>
          <button onClick={() => setViewMode('calendar')} className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${viewMode === 'calendar' ? 'bg-white text-taxera-charcoal shadow-sm' : 'text-slate-400'}`} data-testid="social-view-calendar">Calendar</button>
        </div>
      </div>

      {viewMode === 'calendar' ? (() => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const getPostsForDay = (day) => {
          const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          return posts.filter(p => {
            const pDate = p.scheduled_at || p.published_at || p.created_at || '';
            return pDate.startsWith(dateStr);
          });
        };
        return (
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="social-calendar">
            <div className="flex items-center justify-between px-5 py-3 border-b border-slate-100">
              <button onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <ChevronDown className="w-4 h-4 rotate-90 text-slate-500" />
              </button>
              <span className="text-sm font-bold text-taxera-charcoal">
                {calendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </span>
              <button onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <ChevronDown className="w-4 h-4 -rotate-90 text-slate-500" />
              </button>
            </div>
            <div className="grid grid-cols-7">
              {dayNames.map(d => (
                <div key={d} className="px-2 py-2 text-center text-2xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-100">{d}</div>
              ))}
              {Array.from({ length: firstDay }, (_, i) => (
                <div key={`empty-${i}`} className="h-24 border-b border-r border-slate-50" />
              ))}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dayPosts = getPostsForDay(day);
                const isToday = new Date().toDateString() === new Date(year, month, day).toDateString();
                return (
                  <div key={day} className={`h-24 p-1.5 border-b border-r border-slate-50 ${isToday ? 'bg-blue-50/50' : ''}`}>
                    <div className={`text-2xs font-semibold mb-1 ${isToday ? 'text-taxera-blue' : 'text-slate-400'}`}>{day}</div>
                    <div className="space-y-0.5">
                      {dayPosts.slice(0, 3).map(p => {
                        const meta = PLATFORM_META[p.platform] || PLATFORM_META.linkedin;
                        return (
                          <div key={p.post_id} className="flex items-center gap-1 px-1.5 py-0.5 rounded text-2xs font-semibold truncate" style={{ backgroundColor: `${meta.color}12`, color: meta.color }}>
                            <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: meta.color }} />
                            <span className="truncate">{p.content?.substring(0, 20) || meta.label}</span>
                          </div>
                        );
                      })}
                      {dayPosts.length > 3 && <div className="text-2xs text-slate-400 pl-1">+{dayPosts.length - 3} more</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })() : (
      <>
      {filteredPosts.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-2xl border border-slate-200">
          <Share2 className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-400">No {filter === 'all' ? '' : filter} social posts yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredPosts.map((post) => {
            const meta = PLATFORM_META[post.platform] || PLATFORM_META.linkedin;
            const PlatformIcon = meta.icon;
            return (
              <div key={post.post_id} className="bg-white rounded-xl border border-slate-200 p-4 flex items-start gap-4" data-testid={`social-post-${post.post_id}`}>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${meta.color}12` }}>
                  <PlatformIcon className="w-4 h-4" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold" style={{ color: meta.color }}>{meta.label}</span>
                    <span className={`text-2xs px-2 py-0.5 rounded-full font-semibold ${
                      post.status === 'published' ? 'bg-emerald-50 text-emerald-600' :
                      post.status === 'scheduled' ? 'bg-amber-50 text-amber-600' :
                      'bg-slate-100 text-slate-500'
                    }`}>{post.status}</span>
                    {post.source_title && <span className="text-2xs text-slate-400 truncate">from: {post.source_title}</span>}
                  </div>
                  <p className="text-sm text-slate-600 line-clamp-2">{post.content}</p>
                  {post.hashtags?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {post.hashtags.map(h => <span key={h} className="text-2xs text-taxera-blue font-medium">#{h}</span>)}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  {post.status === 'draft' && (
                    <button onClick={() => handlePublish(post.post_id)}
                      className="p-2 rounded-lg bg-emerald-50 text-emerald-600 hover:bg-emerald-100 transition-colors"
                      title="Mark as published" data-testid={`social-publish-${post.post_id}`}>
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => handleDelete(post.post_id)}
                    className="p-2 rounded-lg bg-red-50 text-red-400 hover:bg-red-100 transition-colors"
                    title="Delete" data-testid={`social-delete-${post.post_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default SocialTab;
